import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Plus, Save } from 'lucide-react';
import api from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';

// Full Tailwind 500-shade palette plus a few neutrals — gives a workable
// swatch grid without us picking favourites for the user. The native HTML
// color input below the grid lets them pick anything else.
const PRESET_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E', '#78716C', '#6B7280', '#1F2937',
];

const PipelineSettings = () => {
  const queryClient = useQueryClient();
  const [localStages, setLocalStages] = useState([]);
  // A closedType change that would rewrite the status of leads in the
  // stage waits here until the admin confirms (server answers 409 first).
  const [pendingResync, setPendingResync] = useState(null);
  // Which stage's color popover is open (null = none). Single-popover-at-a-time
  // keeps the layout simple; clicking elsewhere closes it via the document
  // mousedown listener below.
  const [openColorFor, setOpenColorFor] = useState(null);

  useEffect(() => {
    if (!openColorFor) return;
    const close = (e) => {
      // Don't close while interacting with anything inside a popover/swatch.
      if (e.target.closest('[data-color-popover]') || e.target.closest('[data-color-trigger]')) return;
      setOpenColorFor(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openColorFor]);

  const { data: pipelines, isLoading: loadingPipelines } = useQuery(
    ['pipelines'],
    () => api.get('/pipelines').then((r) => r.data)
  );

  const pipeline = pipelines?.[0]; // MVP: single pipeline
  const pipelineId = pipeline?._id;

  const { data: stages, isLoading: loadingStages } = useQuery(
    ['stages', pipelineId],
    () => api.get(`/pipelines/${pipelineId}/stages`).then((r) => r.data),
    { enabled: !!pipelineId }
  );

  useEffect(() => {
    if (stages) setLocalStages(stages);
  }, [stages]);

  const updateStageMutation = useMutation(
    ({ id, patch }) => api.patch(`/pipelines/stages/${id}`, patch).then((r) => r.data),
    {
      onSuccess: (data) => {
        setPendingResync(null);
        if (data?.resyncedLeads) {
          toast.success(`${data.resyncedLeads} lead${data.resyncedLeads === 1 ? '' : 's'} updated to match the stage`);
        }
        queryClient.invalidateQueries(['stages', pipelineId]);
      },
      onError: (err, vars) => {
        if (err.response?.status === 409 && err.response?.data?.code === 'stage_resync_confirm_required') {
          setPendingResync({ ...vars, summary: err.response.data.summary });
          return;
        }
        setPendingResync(null);
        toast.error(err.response?.data?.error || 'Update failed');
      }
    }
  );

  // Where each status puts a lead: Customers, Leads or Do Not Call.
  const STATUS_WORDS = {
    won: 'Won (listed in Customers)',
    lost: 'Lost (listed in Leads)',
    do_not_call: 'Do Not Call (listed in Do Not Call)',
    new: 'New (listed in Leads)',
    contacted: 'Contacted (listed in Leads)',
    proposal_sent: 'Proposal Sent (listed in Leads)',
    quote_sent: 'Quote Sent (listed in Leads)',
    negotiation: 'Negotiation (listed in Leads)',
  };
  const resyncMessage = (() => {
    const sum = pendingResync?.summary;
    if (!sum) return '';
    const stageName = localStages.find((st) => st._id === pendingResync.id)?.name || 'this stage';
    const parts = Object.entries(sum.byStatus).map(([status, n]) => `${n} to ${STATUS_WORDS[status] || status}`);
    const lines = [`${sum.total} lead${sum.total === 1 ? '' : 's'} in "${stageName}" will change status: ${parts.join(', ')}.`];
    if (sum.leavingWon) lines.push(`${sum.leavingWon} customer${sum.leavingWon === 1 ? '' : 's'} will lose their won date.`);
    if (sum.byStatus.do_not_call) lines.push('Leads put on Do Not Call can only be taken off one by one, with a reason.');
    return lines.join(' ');
  })();

  const addStageMutation = useMutation(
    (body) => api.post(`/pipelines/${pipelineId}/stages`, body).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Stage added');
        queryClient.invalidateQueries(['stages', pipelineId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Add failed')
    }
  );

  const deleteStageMutation = useMutation(
    (id) => api.delete(`/pipelines/stages/${id}`).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Stage deleted');
        queryClient.invalidateQueries(['stages', pipelineId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed')
    }
  );

  const reorderMutation = useMutation(
    (order) => api.post(`/pipelines/${pipelineId}/stages/reorder`, { order }).then((r) => r.data),
    {
      onSuccess: () => queryClient.invalidateQueries(['stages', pipelineId]),
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Reorder failed');
        queryClient.invalidateQueries(['stages', pipelineId]);
      }
    }
  );

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(localStages);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const withOrder = reordered.map((s, idx) => ({ ...s, order: idx }));
    setLocalStages(withOrder);
    reorderMutation.mutate(withOrder.map((s) => ({ id: s._id, order: s.order })));
  };

  const handleFieldBlur = (stage, field, value) => {
    if (stage[field] === value) return;
    updateStageMutation.mutate({ id: stage._id, patch: { [field]: value } });
  };

  const handleClosedToggle = (stage, isClosed) => {
    if (isClosed && !stage.closedType) {
      updateStageMutation.mutate({ id: stage._id, patch: { isClosed: true, closedType: 'won' } });
    } else {
      updateStageMutation.mutate({ id: stage._id, patch: { isClosed, closedType: isClosed ? stage.closedType : null } });
    }
  };

  const confirmResync = () => {
    if (!pendingResync) return;
    updateStageMutation.mutate({ id: pendingResync.id, patch: { ...pendingResync.patch, confirm: true } });
  };

  const handleAddStage = () => {
    const name = window.prompt('New stage name:');
    if (!name || !name.trim()) return;
    addStageMutation.mutate({ name: name.trim(), color: '#6B7280' });
  };

  const handleDelete = (stage) => {
    if (!window.confirm(`Delete stage "${stage.name}"?`)) return;
    deleteStageMutation.mutate(stage._id);
  };

  if (loadingPipelines || loadingStages) {
    return <div className="p-8 text-gray-500">Loading pipeline settings...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <ConfirmationModal
        isOpen={!!pendingResync}
        onClose={() => setPendingResync(null)}
        onConfirm={confirmResync}
        title="Change the leads in this stage?"
        message={resyncMessage}
        confirmText="Change stage and leads"
        isDanger={!!pendingResync?.summary?.byStatus?.do_not_call}
        isLoading={updateStageMutation.isLoading}
      />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pipeline Settings</h1>
          <p className="text-gray-600 mt-1">
            {pipeline ? pipeline.name : 'Sales Pipeline'} — drag to reorder
          </p>
        </div>
        <button
          onClick={handleAddStage}
          className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Stage
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 bg-gray-50">
          <div className="col-span-1"></div>
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Color</div>
          <div className="col-span-4">Closed</div>
          <div className="col-span-1 text-right"></div>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="stages">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {localStages.map((stage, index) => (
                  <Draggable key={stage._id} draggableId={stage._id} index={index}>
                    {(prov, snapshot) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        className={`grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-gray-100 last:border-b-0 ${snapshot.isDragging ? 'bg-blue-50 shadow' : 'hover:bg-gray-50'}`}
                      >
                        <div className="col-span-1" {...prov.dragHandleProps}>
                          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                        </div>
                        <div className="col-span-4">
                          <input
                            type="text"
                            defaultValue={stage.name}
                            onBlur={(e) => handleFieldBlur(stage, 'name', e.target.value)}
                            className="w-full px-2 py-1 border border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none rounded text-sm"
                          />
                          {stage.isDefault && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-400">default</span>
                          )}
                        </div>
                        <div className="col-span-2 relative">
                          <button
                            type="button"
                            data-color-trigger
                            onClick={() => setOpenColorFor(openColorFor === stage._id ? null : stage._id)}
                            className="flex items-center gap-2 px-2 py-1 border border-gray-200 hover:border-gray-300 rounded text-xs"
                          >
                            <span
                              className="w-5 h-5 rounded-full border border-gray-200"
                              style={{ backgroundColor: stage.color }}
                            />
                            <span className="font-mono">{stage.color}</span>
                          </button>
                          {openColorFor === stage._id && (
                            <div
                              data-color-popover
                              className={`absolute z-20 left-0 ${index >= localStages.length - 2 ? 'bottom-full mb-1' : 'top-full mt-1'} p-3 bg-white rounded-lg shadow-lg border border-gray-200 w-56`}
                            >
                              <div className="grid grid-cols-5 gap-2 mb-3">
                                {PRESET_COLORS.map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => {
                                      handleFieldBlur(stage, 'color', c);
                                      setOpenColorFor(null);
                                    }}
                                    title={c}
                                    style={{ backgroundColor: c }}
                                    className={`w-8 h-8 rounded-full border-2 transition ${stage.color === c ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'}`}
                                  />
                                ))}
                              </div>
                              <label className="flex items-center gap-2 text-[11px] text-gray-600 border-t border-gray-100 pt-2">
                                Custom
                                <input
                                  type="color"
                                  value={stage.color}
                                  onChange={(e) => handleFieldBlur(stage, 'color', e.target.value)}
                                  className="w-7 h-7 cursor-pointer rounded"
                                />
                                <span className="font-mono text-gray-500">{stage.color}</span>
                              </label>
                            </div>
                          )}
                        </div>
                        <div className="col-span-4 flex items-center gap-3 text-sm">
                          <label className="inline-flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              checked={stage.isClosed}
                              onChange={(e) => handleClosedToggle(stage, e.target.checked)}
                            />
                            <span>Closed</span>
                          </label>
                          {stage.isClosed && (
                            <select
                              value={stage.closedType || 'won'}
                              onChange={(e) => handleFieldBlur(stage, 'closedType', e.target.value)}
                              className="text-xs border border-gray-200 rounded px-1 py-0.5"
                            >
                              <option value="won">Won</option>
                              <option value="lost">Lost</option>
                              <option value="do_not_call">Do Not Call</option>
                            </select>
                          )}
                        </div>
                        <div className="col-span-1 text-right">
                          <button
                            onClick={() => handleDelete(stage)}
                            disabled={stage.isDefault}
                            title={stage.isDefault ? 'Default stages cannot be deleted' : 'Delete'}
                            className="text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Edits save automatically on blur. Reordering updates immediately.
      </p>
    </div>
  );
};

export default PipelineSettings;
